import { useState } from "react";
import type { FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/lib/AuthContext";
import { GoogleButton } from "@/components/ui/GoogleButton";
import { usePageTitle } from "@/hooks/usePageTitle";
import logo from "@/assets/famibelleza_logo.png";

const inputClass =
  "w-full rounded-lg border border-app-border bg-app-control px-3 py-2 text-foreground focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand";

export default function Register() {
  const { t } = useTranslation();
  usePageTitle(t("register.title"));
  const { register } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password !== confirmPassword) {
      setError(t("register.passwordsMismatch"));
      return;
    }

    setSubmitting(true);
    try {
      await register({ email, password });
      navigate("/login");
    } catch (err) {
      setError(err instanceof Error ? err.message : t("register.error"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="relative flex items-center justify-center py-12 px-4 overflow-hidden">
      <img src={logo} alt="" className="absolute top-0 w-1/2 h-full object-contain z-0 opacity-10 blur-md" />
      <div className="relative z-10 max-w-md w-full bg-app-surface text-foreground border border-app-border rounded-2xl shadow-md p-8 space-y-6">
        <div>
          <h1 className="text-center text-3xl font-extrabold text-brand">{t("register.title")}</h1>
          <p className="mt-2 text-center text-sm text-app-muted">
            {t("register.haveAccount")}{" "}
            <Link to="/login" className="font-medium text-brand hover:text-brand/80">{t("register.logIn")}</Link>
          </p>
        </div>

        <form onSubmit={onSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <label htmlFor="email" className="text-sm font-medium text-foreground">{t("common.email")}</label>
            <input
              id="email"
              type="email"
              required
              className={inputClass}
              value={email}
              onChange={e => setEmail(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor="password" className="text-sm font-medium text-foreground">{t("common.password")}</label>
            <input
              id="password"
              type="password"
              required
              minLength={10}
              className={inputClass}
              value={password}
              onChange={e => setPassword(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor="confirmPassword" className="text-sm font-medium text-foreground">{t("common.confirmPassword")}</label>
            <input
              id="confirmPassword"
              type="password"
              required
              className={inputClass}
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
            />
          </div>

          {error && (
            <div className="rounded-md bg-red-500/10 p-3">
              <p className="text-sm font-medium text-red-700 dark:text-red-300">{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full py-3 px-4 bg-brand text-white font-semibold rounded-2xl cursor-pointer hover:bg-brand/90 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
          >
            {submitting ? t("register.submitting") : t("register.submit")}
          </button>
        </form>

        <GoogleButton />
      </div>
    </div>
  );
}
